/**
 * API client for account management endpoints.
 * Routes: /api/mgmt/accounts
 */

import { apiFetch } from './client';

export interface Account {
  accountId: string;
  accountName: string;
  roleArn: string;
  externalId?: string;
  region: string;
  status: 'active' | 'disabled' | 'pending';
  lastTestedAt?: string;
  lastTestResult?: string;
  lastTestError?: string;
  tpmLimit?: number;
  rpmLimit?: number;
  models?: string[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface CreateAccountData {
  accountId: string;
  accountName: string;
  roleArn: string;
  externalId?: string;
  region: string;
  tpmLimit?: number;
  rpmLimit?: number;
}

export interface UpdateAccountData {
  accountName?: string;
  roleArn?: string;
  externalId?: string;
  region?: string;
  status?: 'active' | 'disabled' | 'pending';
  tpmLimit?: number;
  rpmLimit?: number;
  models?: string[];
}

export interface TestConnectionResult {
  success: boolean;
  error?: string;
  availableModels?: string[];
}

export interface BedrockModel {
  modelId: string;
  modelName: string;
  providerName: string;
  inputModalities?: string[];
  outputModalities?: string[];
  inferenceTypesSupported?: string[];
}

export async function listAccounts(token: string): Promise<Account[]> {
  const data = await apiFetch<{ accounts: Account[] }>('/api/mgmt/accounts', token);
  return Array.isArray(data) ? data : (data.accounts ?? []);
}

export async function getAccount(accountId: string, token: string): Promise<Account> {
  const data = await apiFetch<{ account: Account }>(
    `/api/mgmt/accounts/${encodeURIComponent(accountId)}`,
    token,
  );
  return data.account;
}

export async function createAccount(data: CreateAccountData, token: string): Promise<Account> {
  const result = await apiFetch<{ account: Account }>('/api/mgmt/accounts', token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return result.account;
}

export async function updateAccount(
  accountId: string,
  data: UpdateAccountData,
  token: string,
): Promise<Account> {
  const result = await apiFetch<{ account: Account }>(
    `/api/mgmt/accounts/${encodeURIComponent(accountId)}`,
    token,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    },
  );
  return result.account;
}

export async function deleteAccount(accountId: string, token: string): Promise<void> {
  return apiFetch<void>(
    `/api/mgmt/accounts/${encodeURIComponent(accountId)}`,
    token,
    {
      method: 'DELETE',
    },
  );
}

export async function testAccountConnection(
  accountId: string,
  token: string,
): Promise<TestConnectionResult> {
  return apiFetch<TestConnectionResult>(
    `/api/mgmt/accounts/${encodeURIComponent(accountId)}/test`,
    token,
    {
      method: 'POST',
    },
  );
}

export async function listAccountModels(
  accountId: string,
  token: string,
): Promise<BedrockModel[]> {
  const data = await apiFetch<{ models: BedrockModel[] }>(
    `/api/mgmt/accounts/${encodeURIComponent(accountId)}/models`,
    token,
  );
  return data.models ?? [];
}
